// Angular imports
import { Injector } from '@angular/core';

// cc-common imports
import { IconConstants, Utils } from 'cc-common';

// Constant imports
import { StringFormatConstants } from '../../_constants/string-format-constants';

// Service imports
import { UserService } from '../../_services/data-services/user.service';
import { EmailSharedService } from '../../_services/shared-services/email-shared.service';
import { HelperService } from '../../_services/shared-services/helper.service';
import { PrecisionService } from '../../_services/shared-services/precision.service';
import { ScreenService } from '../../_services/shared-services/screen.service';
import { ServerErrorService } from '../../_services/shared-services/server-error.service';
import { WorkOrderHelperService } from '../_services/work-order-helper.service';
import { WorkOrderService } from '../_services/work-order.service';

// model imports
import { WorkOrderReferencesParameterDto } from '../_models/work-order-references-parameter-dto';
import { WorkOrderReferencesResultDto } from '../_models/work-order-references-result-dto';
import { WorkOrderRawDto } from '../_models/work-order-raw-dto';

export abstract class WorkOrderBaseComponent {
    protected userService: UserService;
    protected helperService: HelperService;
    protected screenService: ScreenService;
    protected errorService: ServerErrorService;
    protected precisionService: PrecisionService;
    protected workOrderHelperService: WorkOrderHelperService;
    protected workOrderService: WorkOrderService;
    protected emailSharedService: EmailSharedService;

    isSmallScreen = false;
    isInternalUser: boolean;
    currencyFormat: string;
    dateFormat: string;
    dateTimeFormat: string;

    showNotes = false;

    references: Array<WorkOrderReferencesResultDto> = [];
    referenceName: string;

    iconLock = IconConstants.LOCK;
    iconKey = IconConstants.KEY;
    iconFlag = IconConstants.FLAG;
    iconAlert = IconConstants.WARN;
    iconNotes = IconConstants.NOTES_AVAILABLE;
    iconEmptyNotes = IconConstants.NOTES_EMPTY;
    iconHistory = IconConstants.HISTORY;
    iconAttachment = IconConstants.ATTACHMENT;
    iconSave = IconConstants.SAVE;
    iconClose = IconConstants.CLOSE;
    iconCheck = IconConstants.CHECK;
    iconCross = IconConstants.CROSS;
    iconCancel = IconConstants.CANCEL;
    iconPDF = IconConstants.PDF_FILE_GRID;
    iconHelp = IconConstants.HELP;
    iconRefresh = IconConstants.REFRESH;
    iconPlus = IconConstants.ADD;
    iconEdit = IconConstants.PEN;
    iconEmail = IconConstants.EMAIL;
    iconCrossCircle = IconConstants.CROSS_CIRCLE;
    iconEllipsish = IconConstants.ELLIPSISH;
    iconDelete = IconConstants.DELETE;
    iconFile = IconConstants.FILE;
    iconUpload = IconConstants.UPLOAD;

    constructor(protected readonly injector: Injector) {
        this.userService = injector.get(UserService);
        this.helperService = injector.get(HelperService);
        this.screenService = injector.get(ScreenService);
        this.errorService = injector.get(ServerErrorService);
        this.precisionService = injector.get(PrecisionService);
        this.workOrderHelperService = injector.get(WorkOrderHelperService);
        this.workOrderService = injector.get(WorkOrderService);
        this.emailSharedService = injector.get(EmailSharedService);

        this.updateScreenSize();

        this.dateFormat = StringFormatConstants.dateFormat;
        this.dateTimeFormat = StringFormatConstants.dateTimeFormat;
        this.currencyFormat = this.precisionService.getCurrencyFormat();
        this.isInternalUser = this.userService.isInternal();
    }

    onNotesClick(): void {
        this.showNotes = true;
    }

    onNotesClose(): void {
        this.showNotes = false;
    }

    emailWorkAuthorization(communityId: number, workOrderId: number): void {
        this.emailSharedService.emailComposerForWorkOrder(communityId, workOrderId);
    }

    populateReferences(
        workOrderInfo: WorkOrderRawDto = null,
        communityId: number = null,
        referenceTypeCode: string = null,
        customerId: string = null
    ): void {
        if (
            Utils.notNullAndDefined(communityId) ||
            (Utils.notNullAndDefined(workOrderInfo) && Utils.notNullAndDefined(workOrderInfo.communityId))
        ) {
            const baseParams: WorkOrderReferencesParameterDto = new WorkOrderReferencesParameterDto();
            baseParams.communityId =
                Utils.notNullAndDefined(workOrderInfo) && Utils.notNullAndDefined(workOrderInfo.communityId)
                    ? workOrderInfo.communityId
                    : communityId;
            baseParams.referenceTypeCode =
                Utils.notNullAndDefined(workOrderInfo) && Utils.notNullAndDefined(workOrderInfo.referenceTypeCode)
                    ? workOrderInfo.referenceTypeCode
                    : referenceTypeCode;

            // Avoiding reference call if referenceType code is 'O' i.e. other.

            if (
                Utils.notNullAndDefined(baseParams.communityId) &&
                Utils.notNullAndDefined(baseParams.referenceTypeCode) &&
                Utils.notEqualsIgnoreCase(baseParams.referenceTypeCode, 'O')
            ) {
                this.workOrderService.getReferences(baseParams).subscribe(
                    (result: WorkOrderReferencesResultDto[]) => {
                        this.references = result;

                        if (
                            Utils.notNullAndDefined(workOrderInfo) &&
                            Utils.notNullAndDefined(workOrderInfo.referenceId)
                        ) {
                            const referenceName = this.references.find((d) =>
                                Utils.equalsIgnoreCase(d.referenceId, workOrderInfo.referenceId)
                            );
                            this.referenceName = Utils.notNullAndDefined(referenceName)
                                ? referenceName.reference
                                : null;
                        }

                        if (
                            Utils.notNullAndDefined(customerId) &&
                            Utils.notNullAndDefined(workOrderInfo) &&
                            Utils.equalsIgnoreCase(workOrderInfo.referenceTypeCode, 'P')
                        ) {
                            const referenceName = this.references.find((d) =>
                                Utils.equalsIgnoreCase(d.attribute, customerId)
                            );

                            if (Utils.notNullAndDefined(referenceName)) {
                                this.referenceName = referenceName.reference;
                                workOrderInfo.referenceId = referenceName.referenceId;
                            }
                        }
                    },
                    (error) => {
                        this.errorService.displayError(error);
                    }
                );
            }
        }
    }

    private updateScreenSize(): void {
        this.isSmallScreen = this.screenService.isSmallScreen();
        this.screenService.changed.subscribe(() => (this.isSmallScreen = this.screenService.isSmallScreen()));
    }
}
